import React, { RefObject, useState } from 'react';
import {
  Camera,
  RefreshCw,
  ScanLine,
  Flashlight,
  SwitchCamera,
  Maximize2,
  CheckCircle2,
  Sparkles,
  QrCode,
  Barcode as BarcodeIcon,
  XCircle,
} from 'lucide-react';
import { ScannerHelpOverlay } from './ScannerHelpOverlay.js';

interface ScannerCameraViewProps {
  videoRef: RefObject<HTMLVideoElement | null>;
  cameraActive: boolean;
  isScanning: boolean;
  showHelp: boolean;
  scanSuccess?: boolean;
  onStartCamera: () => void;
  onShowHelp: (show: boolean) => void;
  onCapture: () => void;
  onCancelScan?: () => void;
  hasTorch?: boolean;
  torchActive?: boolean;
  onToggleTorch?: () => void;
  hasMultipleCameras?: boolean;
  onSwitchCamera?: () => void;
  zoomLevel?: number;
  onZoomChange?: (zoom: number) => void;
  isHardwareZoom?: boolean;
  onRefocus?: () => void;
}

export const ScannerCameraView: React.FC<ScannerCameraViewProps> = ({
  videoRef,
  cameraActive,
  isScanning,
  showHelp,
  scanSuccess = false,
  onStartCamera,
  onShowHelp,
  onCapture,
  onCancelScan,
  hasTorch = false,
  torchActive = false,
  onToggleTorch,
  hasMultipleCameras = false,
  onSwitchCamera,
  zoomLevel = 1,
  onZoomChange,
  isHardwareZoom = false,
  onRefocus,
}) => {
  // Area scanning profile: 'wide' (full camera sensor), 'barcode' (panoramic 1D), 'qr' (square 2D)
  const [reticleMode, setReticleMode] = useState<'wide' | 'barcode' | 'qr'>('wide');

  const getReticleDimensions = () => {
    switch (reticleMode) {
      case 'barcode':
        return 'w-[94%] sm:w-[96%] h-[40%] sm:h-[44%] max-w-xl';
      case 'qr':
        return 'w-[72%] sm:w-[75%] h-[72%] sm:h-[75%] max-w-xs aspect-square';
      case 'wide':
      default:
        return 'w-[90%] sm:w-[94%] h-[78%] sm:h-[82%] max-w-2xl';
    }
  };

  return (
    <div className="flex flex-col items-center w-full">
      {/* Main Wide Camera Viewport */}
      <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] max-h-[52vh] bg-slate-950 rounded-xl sm:rounded-2xl overflow-hidden shadow-xl border border-slate-800 flex items-center justify-center">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            transform: !isHardwareZoom && zoomLevel > 1 ? `scale(${zoomLevel})` : undefined,
            filter: 'contrast(1.12) saturate(1.08) brightness(1.02)',
            transition: 'transform 0.2s ease-out',
          }}
          className="w-full h-full object-cover"
        />

        {/* Ambient Dark Mask with Transparent Target Cutout */}
        <div
          onClick={() => onRefocus?.()}
          className="absolute inset-0 pointer-events-auto cursor-pointer flex items-center justify-center p-2.5 sm:p-4 bg-black/25"
          title="Ketuk layar untuk memfokuskan kamera ulang"
        >
          {/* Dynamic Target Viewfinder Box */}
          <div
            className={`relative rounded-xl transition-all duration-300 flex items-center justify-center ${getReticleDimensions()} ${
              scanSuccess
                ? 'border-3 border-emerald-400 bg-emerald-500/10 shadow-[0_0_30px_rgba(52,211,153,0.6)]'
                : 'border-2 border-indigo-400/70 shadow-[0_0_20px_rgba(99,102,241,0.25)]'
            }`}
          >
            {/* 4 Illuminated Corner Target Brackets */}
            <div
              className={`absolute -top-1 -left-1 w-5 h-5 border-t-3 border-l-3 rounded-tl-lg transition-colors duration-200 ${
                scanSuccess ? 'border-emerald-400' : 'border-indigo-400'
              }`}
            />
            <div
              className={`absolute -top-1 -right-1 w-5 h-5 border-t-3 border-r-3 rounded-tr-lg transition-colors duration-200 ${
                scanSuccess ? 'border-emerald-400' : 'border-indigo-400'
              }`}
            />
            <div
              className={`absolute -bottom-1 -left-1 w-5 h-5 border-b-3 border-l-3 rounded-bl-lg transition-colors duration-200 ${
                scanSuccess ? 'border-emerald-400' : 'border-indigo-400'
              }`}
            />
            <div
              className={`absolute -bottom-1 -right-1 w-5 h-5 border-b-3 border-r-3 rounded-br-lg transition-colors duration-200 ${
                scanSuccess ? 'border-emerald-400' : 'border-indigo-400'
              }`}
            />

            {/* Subtle Crosshair Target in the Center */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-30">
              <div className="w-3.5 h-0.5 bg-white" />
              <div className="h-3.5 w-0.5 bg-white absolute" />
            </div>

            {/* Animated Laser Scanning Line */}
            {cameraActive && !scanSuccess && (
              <div className="absolute inset-x-2 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent shadow-[0_0_12px_rgba(34,211,238,0.9)] animate-pulse" />
            )}

            {/* Success Confirmation Badge */}
            {scanSuccess && (
              <div className="absolute inset-0 bg-emerald-950/40 backdrop-blur-2xs flex flex-col items-center justify-center text-emerald-300 font-bold text-sm animate-in fade-in zoom-in-95 duration-150 rounded-xl">
                <CheckCircle2 className="w-9 h-9 text-emerald-400 mb-1 drop-shadow-md animate-bounce" />
                <span>Kode Terverifikasi!</span>
              </div>
            )}
          </div>
        </div>

        {/* Top-Right On-Screen Hardware Controls (Torch / Camera Flip / Zoom) - Hidden when Help is open */}
        {cameraActive && !showHelp && (
          <div className="absolute top-2.5 right-2.5 flex items-center gap-1.5 z-20">
            {/* Quick Zoom Buttons (1x, 2x Makro, 3x Mikro) */}
            {onZoomChange && (
              <div className="flex items-center bg-black/50 backdrop-blur-md p-0.5 rounded-lg border border-white/20 text-white text-[10px] font-bold">
                <button
                  type="button"
                  onClick={() => onZoomChange(1)}
                  className={`px-1.5 py-1 rounded-md transition cursor-pointer ${
                    zoomLevel === 1 ? 'bg-indigo-600 text-white' : 'hover:bg-white/20 text-white/80'
                  }`}
                  title="Zoom 1x (Standar)"
                >
                  1x
                </button>
                <button
                  type="button"
                  onClick={() => onZoomChange(2)}
                  className={`px-1.5 py-1 rounded-md transition cursor-pointer flex items-center gap-0.5 ${
                    zoomLevel === 2 ? 'bg-indigo-600 text-white shadow-xs' : 'hover:bg-white/20 text-white/80'
                  }`}
                  title="Zoom 2x (Makro - Sangat Bagus untuk Barcode/QR Kecil)"
                >
                  <span>2x</span>
                  <span className="hidden sm:inline text-[9px] font-normal opacity-90">Makro</span>
                </button>
                <button
                  type="button"
                  onClick={() => onZoomChange(3)}
                  className={`px-1.5 py-1 rounded-md transition cursor-pointer flex items-center gap-0.5 ${
                    zoomLevel === 3 ? 'bg-indigo-600 text-white shadow-xs' : 'hover:bg-white/20 text-white/80'
                  }`}
                  title="Zoom 3x (Mikro - Untuk Barcode Ekstra Kecil / Jauh)"
                >
                  <span>3x</span>
                  <span className="hidden sm:inline text-[9px] font-normal opacity-90">Mikro</span>
                </button>
              </div>
            )}

            {onRefocus && (
              <button
                type="button"
                onClick={onRefocus}
                className="p-1.5 sm:p-2 rounded-lg bg-black/40 hover:bg-black/60 text-white backdrop-blur-md border border-white/20 transition cursor-pointer flex items-center justify-center active:scale-95"
                title="Fokuskan Ulang Lensa Kamera"
              >
                <ScanLine className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cyan-300" />
              </button>
            )}

            {hasTorch && onToggleTorch && (
              <button
                type="button"
                onClick={onToggleTorch}
                className={`p-1.5 sm:p-2 rounded-lg backdrop-blur-md border transition cursor-pointer flex items-center justify-center ${
                  torchActive
                    ? 'bg-amber-400 text-slate-950 border-amber-300 shadow-md scale-105'
                    : 'bg-black/40 hover:bg-black/60 text-white border-white/20'
                }`}
                title={torchActive ? 'Matikan Senter' : 'Nyalakan Senter Kamera'}
              >
                <Flashlight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            )}

            {hasMultipleCameras && onSwitchCamera && (
              <button
                type="button"
                onClick={onSwitchCamera}
                className="p-1.5 sm:p-2 rounded-lg bg-black/40 hover:bg-black/60 text-white backdrop-blur-md border border-white/20 transition cursor-pointer flex items-center justify-center"
                title="Ganti Kamera (Depan / Belakang)"
              >
                <SwitchCamera className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            )}
          </div>
        )}

        {/* Top-Left Engine & Macro Indicator - Hidden when Help is open */}
        {cameraActive && !showHelp && (
          <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 z-20">
            <div className="flex items-center gap-1.5 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-black/50 backdrop-blur-md border border-white/15 text-white text-[9.5px] sm:text-[10px] font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping inline-block" />
              <span className="font-semibold">Auto-Scan Aktif</span>
            </div>
            {zoomLevel > 1 && (
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-600/90 backdrop-blur-md border border-indigo-400 text-white text-[9.5px] font-bold shadow-xs animate-in fade-in zoom-in-95">
                <span>🔍 {zoomLevel}x Makro</span>
              </div>
            )}
          </div>
        )}

        {/* Bottom Floating Focus Mode Pill Inside Camera - Hidden when Help is open */}
        {cameraActive && !showHelp && (
          <div className="absolute bottom-2.5 inset-x-0 flex justify-center z-20 px-2 pointer-events-auto">
            <div className="flex items-center gap-0.5 bg-black/60 backdrop-blur-md p-1 rounded-full border border-white/20 shadow-lg text-[10.5px]">
              <button
                type="button"
                onClick={() => setReticleMode('wide')}
                className={`px-2.5 py-0.5 rounded-full transition cursor-pointer flex items-center gap-1 font-semibold ${
                  reticleMode === 'wide'
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'text-white/80 hover:text-white'
                }`}
                title="Area Luas (Sensor Penuh)"
              >
                <Maximize2 className="w-3 h-3" />
                <span>Area Luas</span>
              </button>
              <button
                type="button"
                onClick={() => setReticleMode('barcode')}
                className={`px-2.5 py-0.5 rounded-full transition cursor-pointer flex items-center gap-1 font-semibold ${
                  reticleMode === 'barcode'
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'text-white/80 hover:text-white'
                }`}
                title="Mode Barcode 1D (Garis Lebar Code 128 / EAN-13)"
              >
                <BarcodeIcon className="w-3 h-3" />
                <span>Barcode 1D</span>
              </button>
              <button
                type="button"
                onClick={() => setReticleMode('qr')}
                className={`px-2.5 py-0.5 rounded-full transition cursor-pointer flex items-center gap-1 font-semibold ${
                  reticleMode === 'qr'
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'text-white/80 hover:text-white'
                }`}
                title="Mode QR Code 2D (Kotak)"
              >
                <QrCode className="w-3 h-3" />
                <span>QR 2D</span>
              </button>
            </div>
          </div>
        )}

        {/* Camera Inactive / Permission Prompt */}
        {!cameraActive && !showHelp && (
          <div className="absolute inset-0 bg-slate-900/95 flex flex-col items-center justify-center p-5 text-center text-white z-30">
            <div className="w-11 h-11 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-300 mb-2.5 shadow-inner">
              <Camera className="w-5 h-5" />
            </div>
            <h4 className="font-bold text-xs sm:text-sm mb-1">Kamera Memerlukan Izin</h4>
            <p className="text-[11px] text-slate-300 max-w-xs mb-3 leading-relaxed">
              Izinkan akses kamera pada peramban Anda untuk memindai barcode atau QR code secara otomatis.
            </p>
            <button
              type="button"
              onClick={onStartCamera}
              className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition cursor-pointer shadow-md"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Aktifkan Kamera Sekarang</span>
            </button>
          </div>
        )}

        {/* Full Viewport Help Overlay Modal (Z-50) */}
        {showHelp && (
          <ScannerHelpOverlay onCloseHelp={() => onShowHelp(false)} />
        )}
      </div>

      {/* Action Row Below Camera (Capture Button, Cancel Button & Help) */}
      <div className="w-full max-w-xl mt-2 flex items-center gap-2">
        {isScanning ? (
          <div className="flex-1 flex items-center gap-2">
            <div className="flex-1 py-2 px-3 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-xl font-medium text-xs flex items-center justify-center gap-2 shadow-2xs">
              <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-600 shrink-0" />
              <span className="truncate">Menganalisis barcode (maks. 3.5 dtk)...</span>
            </div>
            {onCancelScan && (
              <button
                type="button"
                onClick={onCancelScan}
                className="py-2 px-3 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-xl font-bold text-xs flex items-center gap-1.5 transition cursor-pointer shadow-2xs shrink-0"
                title="Batalkan proses analisis"
              >
                <XCircle className="w-3.5 h-3.5 text-rose-600" />
                <span>Batalkan</span>
              </button>
            )}
          </div>
        ) : (
          <button
            id="btn-scan-capture"
            type="button"
            disabled={!cameraActive}
            onClick={onCapture}
            className="flex-1 py-2 px-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 shadow-xs transition disabled:opacity-50 cursor-pointer"
          >
            <ScanLine className="w-3.5 h-3.5" />
            <span>Pindai Manual (Ambil Frame)</span>
          </button>
        )}

        <button
          type="button"
          onClick={() => onShowHelp(!showHelp)}
          className={`px-3 py-2 rounded-xl font-semibold text-xs flex items-center gap-1.5 transition shrink-0 cursor-pointer ${
            showHelp
              ? 'bg-indigo-100 text-indigo-700 border border-indigo-200 font-bold'
              : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
          }`}
          title={showHelp ? 'Tutup Petunjuk' : 'Petunjuk & Bantuan Pemindaian'}
        >
          <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
          <span>{showHelp ? 'Tutup Bantuan' : 'Bantuan'}</span>
        </button>
      </div>
    </div>
  );
};
